import {
  Box,
  Stack,
  Button,
  useTheme,
  FormControl,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import { CountrySelect } from 'src/components/country-select';
import InioTextField from 'src/components/inio-text-field/InioTextField';
import { PhoneInput } from 'src/components/phone-input';
import { useTranslate } from 'src/locales';
import type * as yup from 'yup';

type Props<T> = {
  columns: Array<{
    name: string;
    type: string;
    placeholder: string;
    label: string;
  }>;
  values: Record<keyof T, any>;
  validations: yup.ObjectSchema<Record<string, any>>;
  onCancel: () => void;
  onSave: (values: T, { setErrors }: FormikHelpers<T>) => Promise<any>;
  isLoading: boolean;
  formId: string;
};

export function EditInformation<T extends Record<string, any>>({
  columns,
  onCancel,
  onSave,
  values,
  validations,
  isLoading,
  formId,
}: Props<T>) {
  const theme = useTheme();
  const { t } = useTranslate();

  return (
    <Formik
      onSubmit={onSave}
      initialValues={values}
      validationSchema={validations}
      enableReinitialize
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} id={formId}>
          <Box
            sx={{
              position: 'relative',
            }}
          >
            {isLoading && (
              <Box
                textAlign="center"
                sx={{
                  m: 1,
                  position: 'absolute',
                  transform: 'translate(-50%, -50%)',
                  top: '30%',
                  left: '50%',
                }}
              >
                <CircularProgress color="warning" size={30} />
              </Box>
            )}

            {columns.map((column) => {
              return (
                <Stack key={column.name} sx={{ marginBottom: theme.spacing(1.5) }}>
                  {/* Text */}
                  {column.type === 'text' && (
                    <FormControl sx={{ gridColumn: 'span 2', opacity: isLoading ? '.3' : '1' }}>
                      <InioTextField
                        formik={formik}
                        variant="standard"
                        showHelperTextOnError
                        {...column}
                      />
                      {!!formik.touched[column.name] && !!formik.errors[column.name] && (
                        <FormHelperText
                          error={!!formik.touched[column.name] && !!formik.errors[column.name]}
                          sx={{ mx: 1.75 }}
                        >
                          {!!formik.errors[column.name]}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}

                  {/* Phone */}
                  {column.type === 'phone' && (
                    <FormControl
                      sx={{ gridColumn: 'span 2', opacity: isLoading ? '.3' : '1', my: 1 }}
                    >
                      <PhoneInput
                        data-test-id="phone-input"
                        fullWidth
                        type="text"
                        label={t('forms.labels.phone')}
                        placeholder={t('forms.placeholders.phone')}
                        onBlur={formik.handleBlur}
                        onChange={(newValue) =>
                          formik.setFieldValue('visibleContactPhone', newValue)
                        }
                        value={formik.values.visibleContactPhone}
                        name="visibleContactPhone"
                        error={
                          !!formik.touched.visibleContactPhone &&
                          !!formik.errors.visibleContactPhone
                        }
                        disabled={isLoading}
                      />
                    </FormControl>
                  )}

                  {/* Country select */}
                  {column.type === 'countrySelect' && (
                    <FormControl
                      sx={{ gridColumn: 'span 2', opacity: isLoading ? '.3' : '1', my: 1 }}
                    >
                      <CountrySelect
                        data-test-id="dialog-select-country"
                        id="country-select"
                        label={t('forms.labels.country')}
                        fullWidth
                        getValue="code"
                        onChange={(e, newValue) => {
                          formik.setFieldValue('countryCode', newValue);
                        }}
                        onBlur={() => formik.setFieldTouched('countryCode', true)}
                        value={formik.values.countryCode}
                        error={!!formik.touched.countryCode && !!formik.errors.countryCode}
                        disabled={isLoading}
                      />
                    </FormControl>
                  )}
                </Stack>
              );
            })}

            <Box sx={{ mt: theme.spacing(3), display: 'flex', justifyContent: 'flex-end' }}>
              <Stack direction="row" spacing={theme.spacing(1.5)}>
                <Button
                  data-test-id="close-btn"
                  variant="outlined"
                  onClick={onCancel}
                  disabled={isLoading}
                  loading={isLoading}
                >
                  {t('buttons.cancel')}
                </Button>
                <Button
                  disabled={isLoading}
                  loading={isLoading}
                  type="submit"
                  form={formId}
                  color="primary"
                  variant="contained"
                >
                  {t('buttons.saveChanges')}
                </Button>
              </Stack>
            </Box>
          </Box>
        </form>
      )}
    </Formik>
  );
}
